import type { Cotizacion, Garantia } from "./types";

// Una fila del bloque «Garantías» del PDF. `grupo` null = generales (toda la
// cotización); si no, el nombre del sitio. `cubre` dice a qué aplica dentro
// del grupo: la cotización, el sitio entero o una pieza.
export interface GarantiaPdf {
  categoria: string;
  anios: number | null;
  notas: string;
  grupo: string | null;
  cubre: string;
}

export const TODA_LA_COTIZACION = "Toda la cotización";
export const TODO_EL_SITIO = "Todo el sitio";

// Una garantía se ofrece solo si está marcada y tiene años (ver Margen.tsx).
const ofrecidas = (lista: Garantia[] | undefined) =>
  (lista ?? []).filter((g) => g.incluida && g.anios != null && g.anios > 0);

const fila = (g: Garantia, grupo: string | null, cubre: string) => ({
  categoria: g.categoria,
  anios: g.anios,
  notas: g.notas,
  grupo,
  cubre,
});

// Lo que imprime el PDF, en orden: generales, y por cada sitio las suyas y
// las de sus piezas. Con «Incluir garantías» apagado no sale ninguna.
export function garantiasDelPdf(c: Cotizacion): GarantiaPdf[] {
  if (!c.incluirGarantias) return [];
  const generales = ofrecidas(c.garantias).map((g) =>
    fila(g, null, TODA_LA_COTIZACION),
  );
  const porSitio = c.sitios.flatMap((s, i) => {
    const grupo = s.nombre.trim() || `Sitio ${i + 1}`;
    return [
      ...ofrecidas(s.garantias).map((g) => fila(g, grupo, TODO_EL_SITIO)),
      ...s.productos.flatMap((pr) =>
        pr.piezas.flatMap((p) => {
          const pieza = p.variante || p.familia || "Pieza sin nombre";
          const cubre = pr.nombre.trim() ? `${pr.nombre} · ${pieza}` : pieza;
          return ofrecidas(p.garantias).map((g) => fila(g, grupo, cubre));
        }),
      ),
    ];
  });
  return [...generales, ...porSitio];
}

// Agrupa las filas para imprimirlas: un bloque por grupo, en el orden en que
// aparecen (generales primero).
export function agruparGarantias(filas: GarantiaPdf[]) {
  const grupos: { grupo: string | null; filas: GarantiaPdf[] }[] = [];
  for (const f of filas) {
    const ultimo = grupos[grupos.length - 1];
    if (ultimo && ultimo.grupo === f.grupo) ultimo.filas.push(f);
    else grupos.push({ grupo: f.grupo, filas: [f] });
  }
  return grupos;
}
